import { Box, Button, Dialog, IconButton, Popover, Table, TableBody, TableCell, TableContainer, TableRow, Typography, useTheme } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { convertToCDNLink, Icons, formatDate, SPACES_LOCATION_REGEXP } from '../../../utils/utilities'
import { toast } from 'react-toastify'
import api from '../../../service/api'
import DataLoadingSpinner from '../../common/DataLoadingSpinner'
import TableHeader from '../../common/TableHeader'
import TryAgainMessageBlock from '../../common/TryAgainMessageBlock'
import { DrawerDocumentViewer } from '../../common/DocumentViewer'
import CustomTable from '../../common/custom-table/CustomTable'


const SubmissionsViewer = ( { selectedValue, assignmentId, open, handleClose } ) => {

    const [error, setError] = useState( null )
    const [loading, setLoading] = useState( true )
    const [previewFile, setPreviewFile] = useState( null )
    const [submissions, setSubmissions] = useState( [] )
    const [submissionFiles, setSubmissionFiles] = useState( [] )
    const [previewingFileDetails, setPreviewingFileDetails] = useState( null )
    const [selectedSubmissionAnchor, setSelectedSubmissionAnchor] = useState( null )

    const { palette, table, border, layout } = useTheme()


    const columns = useMemo( () => [
        { name: "Student", field: "student_name", padding: "10px 20px" },
        { name: "AUID", field: "assignment_submitted_by", padding: "10px 20px" },
        { name: "Submitted on", width: "150px", field: "assignment_submitted_on", dataType: "datetime", renderCell: row => formatDate( row.assignment_submitted_on ), padding: "10px 20px" },
        { name: "Marks Assigned", width: "180px", field: "assignment_marks", renderCell: row => row.assignment_marks || "Not assigned yet!", padding: "10px 20px" },
        {
            name: "Submitted file", width: "180px", field: "submissions", renderCell: row => (
                <Button onClick={e => {
                    setSubmissionFiles( row.assignment_submission_file_paths )
                    setSelectedSubmissionAnchor( e.currentTarget )
                }} size="small" color="primary" sx={{ textTransform: "capitalize" }}  >View Submissions</Button>
            ), align: "right", padding: "10px 20px"
        },
    ], [] )

    const viewSubmission = async ( value ) => {
        try {
            const { data: { data } } = await api.downloadAssignment( value, true )
            const fileDetails = value.match( SPACES_LOCATION_REGEXP ).groups
            console.log( fileDetails )

            setPreviewingFileDetails( fileDetails )
            setPreviewFile( convertToCDNLink( data ) )
        } catch ( err ) {
            console.log( err )
            toast( err?.response?.data?.message || `An error occurred while viewing the submission!` )
        }
    }

    const getData = useCallback( async () => {
        try {
            const { data: { data } } = await api.fetchAssignmentSubmission( assignmentId )
            setSubmissions( data )
            setError( null )
        } catch ( err ) {
            setError( { code: err?.response?.status || 500, err: err?.response?.data?.message || "Something went wrong while fetching submissions." } )
        } finally {
            setLoading( false )
        }
    }, [assignmentId] )

    useEffect( () => {
        getData()
    }, [getData] )

    return (
        <Dialog onClose={handleClose} aria-labelledby="simple-dialog-title" open={open} slotProps={{
            paper: { sx: { width: "95%", maxWidth: "1024px", display: "flex", flexDirection: "column", gap: layout.sectionsGap, padding: layout.pagePadding } }
        }}>
            <Popover transitionDuration={0} sx={{ bgcolor: "rgba(48,48,48,0.6)" }} slotProps={{ paper: { sx: { overflow: "auto" } } }} anchorOrigin={{ vertical: "top", horizontal: "left" }} transformOrigin={{ vertical: "top", horizontal: "right" }} onClose={() => setSubmissionFiles( [] )} anchorEl={selectedSubmissionAnchor} open={submissionFiles?.length > 0}>
                <Typography variant='subtitle2' display="block" borderBottom={border[1]} padding="10px">Submissions</Typography>
                <Box maxHeight="380px" overflow="auto" className="custom-scrollbar">
                    {submissionFiles.map( item => {
                        const fileDetails = item.match( SPACES_LOCATION_REGEXP ).groups
                        return (
                            <Box display="flex" gap="20px" alignItems="center" key={item} padding="5px 10px" color="GrayText" borderBottom={border[1]}>
                                <Typography onClick={() => viewSubmission( item )} fontSize="12px" variant='subtitle2'>{decodeURIComponent( fileDetails.filefullname )}</Typography>
                                <IconButton size='small' onClick={() => viewSubmission( item )}>{Icons.small.VisibilityIcon}</IconButton>
                            </Box>
                        )
                    } )}
                </Box>
            </Popover>
            {previewFile && previewingFileDetails && <DrawerDocumentViewer loadingText='Loading submission' type={previewingFileDetails.fileextention} fileName={decodeURIComponent( previewingFileDetails.filefullname )} setPreviewingFileDetails={setPreviewingFileDetails} setPreviewFile={setPreviewFile} file={previewFile} />}

            {selectedValue && <Box>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box>
                        <Typography variant="h1" fontWeight="600" component="p" fontSize="16px">{selectedValue.assignment_name} submissions</Typography>
                        <Typography variant="subtitle2" fontSize="12px" color='textSecondary' >Due Date: {new Date( selectedValue.assignment_due_date ).toLocaleDateString( "en-IN", { day: "2-digit", month: "2-digit", year: "numeric" } ).replace( /\//g, "-" )}</Typography>
                    </Box>
                    <IconButton onClick={handleClose}>{Icons.default.CloseIcon}</IconButton>
                </Box>
            </Box>}
            <Box>
                {submissions.length > 0 && <Typography fontSize="14px" gutterBottom><em><strong>{submissions.length}</strong> Submissions received</em></Typography>}
                {selectedValue && !loading && !error && submissions.length > 0 && <CustomTable tableKey="aasvl" pageSize={50} tableStyles={{ maxHeight: "450px" }} data={submissions} columns={columns} />}

                {loading && !error && <DataLoadingSpinner waitingMessage="Please wait while we fetch assignment's submissions..." />}

                {error && <TryAgainMessageBlock err={error.err} code={error.code} getData={getData} loading={loading} />}
            </Box>
        </Dialog>
    )
}

export default SubmissionsViewer
